import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Images } from "lucide-react";
import { toast } from "sonner";

const downscale = (file, MAX = 800) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (Math.max(width, height) > MAX) {
          const s = MAX / Math.max(width, height);
          width = Math.round(width * s);
          height = Math.round(height * s);
        }
        const c = document.createElement("canvas");
        c.width = width;
        c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const rowNumberFromName = (name) => {
  const m = name.replace(/\.[^.]+$/, "").match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
};

export default function BulkPhotoUpload({ photoFields, rowCount, onUpload, onAssign, toastSuffix = "", size = "default", testId = "bulk-photo" }) {
  const [open, setOpen] = useState(false);
  const [fieldKey, setFieldKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const fileRef = useRef(null);

  if (!photoFields.length) return null;

  const openDialog = () => {
    setFieldKey(photoFields[0].key);
    setProgress("");
    setOpen(true);
  };

  const processFiles = async (files) => {
    if (!files.length || busy) return;
    setBusy(true);
    let done = 0;
    const skipped = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress(`Uploading ${i + 1}/${files.length}...`);
      const num = rowNumberFromName(f.name);
      if (num == null || num < 1 || num > rowCount) {
        skipped.push(f.name);
        continue;
      }
      try {
        const dataUrl = await downscale(f);
        const pid = await onUpload(dataUrl);
        onAssign(num - 1, fieldKey, pid, dataUrl);
        done++;
      } catch {
        skipped.push(f.name);
      }
    }
    setBusy(false);
    setProgress("");
    if (done) toast.success(`${done} photo${done > 1 ? "s" : ""} uploaded${toastSuffix}`);
    if (skipped.length) toast.warning(`Skipped: ${skipped.join(", ")}`);
    if (done) setOpen(false);
  };

  return (
    <>
      <Button variant="outline" size={size} onClick={openDialog} data-testid={`${testId}-btn`} className="gap-1.5">
        <Images className="h-4 w-4" /> Bulk Photos
      </Button>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="max-w-sm" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>Bulk Photo Upload</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Photo ke file naam me row number hona chahiye — jaise <b>1.jpg</b> row 1 par aur <b>student_25.png</b> row 25 par lag jayegi. Ek saath kai photos select kar sakte hain.
            </p>
            <div className="space-y-1">
              <p className="text-xs font-medium">Photo column</p>
              <Select value={fieldKey} onValueChange={setFieldKey}>
                <SelectTrigger data-testid={`${testId}-field-select`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {photoFields.map((f) => (
                    <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <input
              type="file"
              accept="image/*"
              multiple
              ref={fileRef}
              className="hidden"
              data-testid={`${testId}-file-input`}
              onChange={(e) => { processFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={busy || !fieldKey}
              data-testid={`${testId}-choose-btn`}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
            >
              {busy ? progress : "Choose Photos"}
            </Button>
            {busy && <p className="text-xs text-center text-muted-foreground" data-testid={`${testId}-progress`}>{progress}</p>}
            <p className="text-[11px] text-muted-foreground">Total rows: {rowCount}. Jis number ki row na ho wo photo skip ho jayegi.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
